import type { AuthProvider } from 'react-admin';

/**
 * Sign-in against the admin API.
 *
 * The token is a signed session from POST /v1/auth/login and goes in
 * localStorage, which is the ordinary react-admin arrangement: the API and the
 * admin are separate origins, so an httpOnly cookie would not be sent with the
 * requests that need it. It is a bearer token - whoever holds it is the admin
 * until it expires - which is exactly why "trust this computer" is something
 * the user opts into rather than the default.
 */

const apiUrl = import.meta.env['VITE_API_URL'] ?? 'http://localhost:3000';

const TOKEN_KEY = 'adminToken';
const IDENTITY_KEY = 'adminIdentity';
const EXPIRY_KEY = 'adminTokenExpires';

export interface Identity {
  id: number;
  email: string;
  name: string;
  /** Whether this account may manage the other administrator accounts. */
  isOwner: boolean;
}

export function getToken(): string | null {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token == null) return null;

  // Expiry is checked here as well as by the API, so a stale token logs the
  // user out on the login screen rather than after a screen full of failures.
  const expires = localStorage.getItem(EXPIRY_KEY);
  if (expires != null && new Date(expires) <= new Date()) {
    clearSession();
    return null;
  }

  return token;
}

function clearSession(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(IDENTITY_KEY);
  localStorage.removeItem(EXPIRY_KEY);
}

export interface LoginParams {
  email: string;
  password: string;
  recaptchaToken?: string;
  trustDevice?: boolean;
}

export const authProvider: AuthProvider = {
  login: async ({ email, password, recaptchaToken, trustDevice }: LoginParams) => {
    const response = await fetch(`${apiUrl}/v1/auth/login`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email, password, recaptchaToken, trustDevice })
    });

    const body = (await response.json().catch(() => ({}))) as {
      token?: string;
      expiresAt?: string;
      identity?: Identity;
      message?: string;
    };

    if (!response.ok || body.token == null) {
      // The API's message is written for the person reading it and says the
      // same thing for every kind of failure on purpose.
      throw new Error(body.message ?? 'Prihlásenie zlyhalo.');
    }

    localStorage.setItem(TOKEN_KEY, body.token);
    if (body.expiresAt != null) localStorage.setItem(EXPIRY_KEY, body.expiresAt);
    if (body.identity != null) localStorage.setItem(IDENTITY_KEY, JSON.stringify(body.identity));
  },

  logout: async () => {
    clearSession();
  },

  checkAuth: async () => {
    if (getToken() == null) throw new Error('Prihláste sa, prosím.');
  },

  checkError: async (error: unknown) => {
    const status = (error as { status?: number }).status;

    // Deliberately not 404. An admin route answers 404 only to a caller who
    // offered no credentials at all; a session the API has stopped accepting
    // gets a 401. Meanwhile 404 is also how a missing record answers, so
    // treating it as a lapsed session would sign the user out for following a
    // link to an order somebody has since deleted.
    if (status === 401 || status === 403) {
      clearSession();
      throw new Error('Vaše prihlásenie vypršalo.');
    }
  },

  getIdentity: async () => {
    const stored = localStorage.getItem(IDENTITY_KEY);
    if (stored == null) throw new Error('Neprihlásený.');

    const identity = JSON.parse(stored) as Identity;
    return { id: identity.id, fullName: identity.name || identity.email };
  },

  /*
   * Drives what the menu offers, and nothing more. The API decides the same
   * question for itself on every request, so a stale or edited value here buys
   * nothing but a menu entry whose endpoints all answer "no such route".
   */
  getPermissions: async () => (isOwner() ? 'owner' : 'admin')
};

/** Whether the signed-in account owns the admin. False when nobody is signed in. */
export function isOwner(): boolean {
  const stored = localStorage.getItem(IDENTITY_KEY);
  if (stored == null) return false;

  try {
    return (JSON.parse(stored) as Identity).isOwner === true;
  } catch {
    return false;
  }
}

/** Whether the sign-in form should show a captcha, and with which site key. */
export async function fetchAuthConfig(): Promise<{ recaptchaSiteKey: string | null; recaptchaEnabled: boolean }> {
  try {
    const response = await fetch(`${apiUrl}/v1/auth/config`);
    if (!response.ok) throw new Error(String(response.status));
    return (await response.json()) as { recaptchaSiteKey: string | null; recaptchaEnabled: boolean };
  } catch {
    // Without this the form would refuse to render at all when the API is
    // briefly unreachable, which is a worse failure than a missing widget.
    return { recaptchaSiteKey: null, recaptchaEnabled: false };
  }
}
